import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { UserProfileMenu } from './components/UserProfileMenu';
import SignInPage from './pages/SignInPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import HospitalListPage from './pages/HospitalListPage';
import HospitalFormPage from './pages/HospitalFormPage';
import HospitalHistoryPage from './pages/HospitalHistoryPage';
import ParameterListPage from './pages/ParameterListPage';
import ParameterFormPage from './pages/ParameterFormPage';
import ParameterHistoryPage from './pages/ParameterHistoryPage';
import ParamFormulaPage from './pages/ParamFormulaPage';
import AgreementListPage from './pages/AgreementListPage';
import AgreementFormPage from './pages/AgreementFormPage';
import AgreementDraftsPage from './pages/AgreementDraftsPage';
import AgreementVersionHistoryPage from './pages/AgreementVersionHistoryPage';
import AgreementDocumentsPage from './pages/AgreementDocumentsPage';
import DataInputPage from './pages/DataInputPage';
import RecalculationPage from './pages/RecalculationPage';
import BulkRecalculationPage from './pages/BulkRecalculationPage';
import NotOkReviewPage from './pages/NotOkReviewPage';
import CalculationErrorsPage from './pages/CalculationErrorsPage';
import CalculationHistoryPage from './pages/CalculationHistoryPage';
import ExportPage from './pages/ExportPage';

function AppLayout({ children }: { children: ReactNode }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  return (
    <div className="app">
      <header className="app-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            className="sidebar-toggle"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            {isSidebarOpen ? '☰' : '☰'}
          </button>
          <h1>Hospital AR/AP Calculation System</h1>
        </div>
        <UserProfileMenu />
      </header>
      <div className="app-body">
        <Sidebar isOpen={isSidebarOpen} />
        <main className={`app-main ${!isSidebarOpen ? 'sidebar-closed' : ''}`}>{children}</main>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<SignInPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <HomePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <AdminPlaceholder />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hospitals"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <HospitalListPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hospitals/create"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <HospitalFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hospitals/:hospitalUuid/edit"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <HospitalFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hospitals/:hospitalUuid/history"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <HospitalHistoryPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/parameters"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <ParameterListPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/parameters/create"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <ParameterFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/parameters/:parameterId/edit"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <ParameterFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/parameters/:parameterId/history"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <ParameterHistoryPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/param-formula"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <ParamFormulaPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agreements"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <AgreementListPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agreements/drafts"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <AgreementDraftsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agreements/create"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <AgreementFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agreements/:agreementId/edit"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <AgreementFormPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agreements/:agreementId/versions"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <AgreementVersionHistoryPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agreements/:agreementId/documents"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <AgreementDocumentsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-input"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <DataInputPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recalculation"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <RecalculationPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulk-recalculation"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <BulkRecalculationPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/not-ok-review"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <NotOkReviewPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/calculation-errors"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <CalculationErrorsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calculation-history"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CalculationHistoryPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/export"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AppLayout>
                  <ExportPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

function Sidebar({ isOpen }: { isOpen: boolean }) {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'ADMIN';

  function navLink(to: string, label: string) {
    const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
    return (
      <Link to={to} className={isActive ? 'sidebar-link active' : 'sidebar-link'}>
        {label}
      </Link>
    );
  }

  return (
    <nav className={`app-sidebar ${!isOpen ? 'closed' : ''}`}>
      <div className="sidebar-section">
        <h4>Operations</h4>
        {navLink('/data-input', 'Data Input & Calculate')}
        {navLink('/calculation-history', 'Calculation History')}
        {navLink('/recalculation', 'Recalculation')}
      </div>
      {isAdmin && (
        <>
          <div className="sidebar-section">
            <h4>Administration</h4>
            {navLink('/hospitals', 'Hospitals')}
            {navLink('/parameters', 'Parameters')}
            {navLink('/param-formula', 'Param Formula')}
            {navLink('/agreements', 'Agreements')}
          </div>
          <div className="sidebar-section">
            <h4>Review &amp; Export</h4>
            {navLink('/admin/not-ok-review', 'NOT OK Review')}
            {navLink('/admin/calculation-errors', 'Calculation Errors')}
            {navLink('/bulk-recalculation', 'Bulk Recalculation')}
            {navLink('/export', 'Export')}
          </div>
        </>
      )}
    </nav>
  );
}

function HomePage() {
  return (
    <div className="home-page">
      <h2>Dashboard</h2>
      <p>Welcome to the Hospital AR/AP Calculation System. Use the sidebar to navigate.</p>
    </div>
  );
}

function AdminPlaceholder() {
  return (
    <div className="home-page">
      <h2>Admin Area</h2>
      <p>Use the sidebar to access admin features.</p>
    </div>
  );
}

export default App;
